import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bell, BellRing, Bug, Check, Fingerprint, LogOut, User } from 'lucide-react';
import { cx } from '../charte';
import { StaffPhoto } from '../Photos';
import type { PushState } from '../../api/push';

/** Menu déroulant de l'en-tête : se ferme au clic à côté et à Échap ; pleine largeur sur téléphone. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

const panel =
  'z-50 overflow-hidden rounded-[20px] border border-line bg-white shadow-pop animate-pop ' +
  'fixed inset-x-3 top-[calc(env(safe-area-inset-top)+64px)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2';

/** Cloche : état des notifications de cet appareil (nouvelles leçons, réservations en ligne). */
export function NotificationsMenu({
  pushState,
  pushBusy,
  pushError,
  onTogglePush,
}: {
  pushState: PushState;
  pushBusy: boolean;
  /** Dernier échec d'activation, affiché sous le bouton. */
  pushError?: string | null;
  onTogglePush: () => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const on = pushState === 'subscribed';
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        className={cx(
          'grid h-10 w-10 place-items-center rounded-pill transition-colors hover:bg-surface-2',
          on ? 'text-primary' : 'text-secondary hover:text-body',
        )}
      >
        {on ? <BellRing size={20} aria-hidden /> : <Bell size={20} aria-hidden />}
      </button>
      {open && (
        <div className={cx(panel, 'sm:w-80')}>
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="text-[14px] font-semibold text-heading">Notifications</h3>
          </div>
          <div className="space-y-3 p-4 text-sm">
            {pushState === 'unsupported' ? (
              <p className="text-secondary">Notifications non disponibles sur ce navigateur.</p>
            ) : on ? (
              <>
                <p className="flex items-center gap-2 font-medium text-body">
                  <Check size={16} className="text-success" aria-hidden />
                  Notifications activées sur cet appareil.
                </p>
                <p className="text-xs leading-relaxed text-secondary">
                  Vous serez prévenu·e des nouvelles leçons planifiées et des réservations en ligne de vos élèves.
                </p>
                <button
                  type="button"
                  onClick={onTogglePush}
                  disabled={pushBusy}
                  className="text-xs font-medium text-secondary hover:text-body disabled:opacity-50"
                >
                  {pushBusy ? '…' : 'Désactiver les notifications'}
                </button>
              </>
            ) : pushState === 'denied' ? (
              <p className="leading-relaxed text-secondary">
                Notifications bloquées. Autorisez-les pour Permio dans les réglages de votre navigateur.
              </p>
            ) : (
              <>
                <p className="leading-relaxed text-body">
                  Activez les notifications pour être prévenu·e des nouvelles leçons et réservations en ligne.
                </p>
                {/* Toujours proposé : même autorisé par le navigateur, l'abonnement a pu échouer (J29). */}
                <button
                  type="button"
                  onClick={onTogglePush}
                  disabled={pushBusy}
                  className="inline-flex h-10 w-full items-center justify-center rounded-pill bg-btn-primary text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:opacity-50"
                >
                  {pushBusy ? '…' : 'Activer les notifications'}
                </button>
                {pushError && (
                  <p role="alert" className="text-xs leading-relaxed text-danger-dark">
                    {pushError}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MenuRow({
  icon,
  label,
  danger = false,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors disabled:cursor-default',
        danger ? 'text-danger-dark hover:bg-danger-light' : 'text-body enabled:hover:bg-surface-light',
      )}
    >
      <span className="flex items-center gap-2.5">
        {icon}
        {label}
      </span>
    </button>
  );
}

/** Menu du compte : profil, FaceID, signaler un problème, déconnexion. */
export function AccountMenu({
  userId,
  fullName,
  email,
  monogram,
  bioSupported,
  bioRegistered,
  bioBusy,
  onOpen,
  onEnableBio,
  onProfile,
  onSupport,
  onLogout,
}: {
  userId?: string;
  fullName: string;
  email?: string | null;
  monogram: string;
  bioSupported: boolean;
  bioRegistered: boolean | null;
  bioBusy: boolean;
  /** Appelé à l'ouverture (lecture de l'état FaceID). */
  onOpen: () => void;
  onEnableBio: () => void;
  onProfile: () => void;
  onSupport: () => void;
  onLogout: () => void;
}) {
  const { open, setOpen, ref } = usePopover();
  useEffect(() => {
    if (open) onOpen();
  }, [open, onOpen]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Mon compte"
        aria-expanded={open}
        className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-avatar text-xs font-semibold text-primary-700 transition-shadow hover:ring-4 hover:ring-primary-50"
      >
        {monogram}
        <StaffPhoto userId={userId} />
      </button>
      {open && (
        <div className={cx(panel, 'sm:w-64')}>
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="truncate text-sm font-semibold text-heading">{fullName}</div>
            {email && <div className="truncate text-xs text-secondary">{email}</div>}
          </div>
          <div className="p-1.5">
            <MenuRow
              icon={<User size={16} className="text-muted" aria-hidden />}
              label="Mon profil"
              onClick={() => {
                setOpen(false);
                onProfile();
              }}
            />
            {bioSupported &&
              (bioRegistered ? (
                <MenuRow icon={<Fingerprint size={16} className="text-success" aria-hidden />} label="FaceID activé" disabled onClick={() => undefined} />
              ) : (
                <MenuRow
                  icon={<Fingerprint size={16} className="text-primary" aria-hidden />}
                  label={bioBusy ? 'Activation…' : 'Activer FaceID'}
                  disabled={bioBusy}
                  onClick={onEnableBio}
                />
              ))}
            <MenuRow
              icon={<Bug size={16} className="text-muted" aria-hidden />}
              label="Signaler un problème"
              onClick={() => {
                setOpen(false);
                onSupport();
              }}
            />
            <div className="my-1 h-px bg-slate-100" />
            <MenuRow icon={<LogOut size={16} aria-hidden />} label="Se déconnecter" danger onClick={onLogout} />
          </div>
        </div>
      )}
    </div>
  );
}
